import type { Session } from '@supabase/supabase-js';

const PASSWORD_RECOVERY_STORAGE_KEY = 'worklynx.auth.passwordRecovery';
const PASSWORD_RECOVERY_SHARED_STORAGE_KEY = 'worklynx.auth.passwordRecovery.shared';
const PASSWORD_RECOVERY_TTL_MS = 30 * 60 * 1000;

export type PasswordRecoveryMarker = {
  startedAt: number;
  expiresAt: number;
  userId?: string | null;
  email?: string | null;
  fullName?: string | null;
};

export function markPasswordRecoveryFlow(session?: Session | null, now = Date.now()) {
  if (typeof window === 'undefined') return;

  const marker: PasswordRecoveryMarker = {
    startedAt: now,
    expiresAt: now + PASSWORD_RECOVERY_TTL_MS,
    userId: session?.user.id ?? null,
    email: session?.user.email ?? null,
    fullName: readFullNameFromMetadata(session?.user.user_metadata),
  };
  const serialized = JSON.stringify(marker);

  window.sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, serialized);
  window.localStorage.setItem(PASSWORD_RECOVERY_SHARED_STORAGE_KEY, serialized);
}

export function clearPasswordRecoveryFlow() {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
  window.localStorage.removeItem(PASSWORD_RECOVERY_SHARED_STORAGE_KEY);
}

export function getPasswordRecoveryFlow(now = Date.now()): PasswordRecoveryMarker | null {
  if (typeof window === 'undefined') return null;

  const rawMarker =
    window.localStorage.getItem(PASSWORD_RECOVERY_SHARED_STORAGE_KEY) ??
    window.sessionStorage.getItem(PASSWORD_RECOVERY_STORAGE_KEY);
  if (!rawMarker) return null;

  const marker = parsePasswordRecoveryMarker(rawMarker);
  if (!marker || now > marker.expiresAt) {
    clearPasswordRecoveryFlow();
    return null;
  }

  return marker;
}

export function hasPasswordRecoveryFlow(now = Date.now()) {
  return getPasswordRecoveryFlow(now) !== null;
}

export function isPasswordRecoveryFlowForUser(userId: string | null | undefined) {
  const marker = getPasswordRecoveryFlow();
  if (!marker) return false;
  if (!marker.userId || !userId) return true;
  return marker.userId === userId;
}

export function currentUrlHasPasswordRecoverySignal() {
  if (typeof window === 'undefined') return false;

  const searchParams = new URLSearchParams(window.location.search);
  if (searchParams.get('type') === 'recovery') return true;

  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const hashParams = new URLSearchParams(hash);
  return hashParams.get('type') === 'recovery';
}

function parsePasswordRecoveryMarker(rawMarker: string): PasswordRecoveryMarker | null {
  const legacyStartedAt = Number(rawMarker);
  if (Number.isFinite(legacyStartedAt)) {
    return {
      startedAt: legacyStartedAt,
      expiresAt: legacyStartedAt + PASSWORD_RECOVERY_TTL_MS,
    };
  }

  try {
    const marker = JSON.parse(rawMarker) as Partial<PasswordRecoveryMarker>;
    if (!Number.isFinite(marker.startedAt) || !Number.isFinite(marker.expiresAt)) {
      return null;
    }

    return {
      startedAt: Number(marker.startedAt),
      expiresAt: Number(marker.expiresAt),
      userId: marker.userId ?? null,
      email: marker.email ?? null,
      fullName: marker.fullName ?? null,
    };
  } catch {
    return null;
  }
}

function readFullNameFromMetadata(metadata: Session['user']['user_metadata'] | undefined) {
  const fullName = metadata?.full_name ?? metadata?.fullName ?? metadata?.name;
  return typeof fullName === 'string' && fullName.trim() ? fullName.trim() : null;
}
